import React, { useRef, cloneElement, ReactElement } from 'react';
import classnames from 'classnames';
import omit from 'omit.js';
import { WebUI, getNid } from "../../wui-core/src/index"
import { setComponentSize } from "../../wui-core/src/componentStyle"
import Icon from '../../wui-icon/src';
import { WithConfigConsumer } from "../../wui-provider/src/context";
import ButtonGroup from '../../wui-button-group/src';
import { ButtonProps, ButtonColors } from "./iButton"

/* const propTypes = {
    /!**
	 * @title 尺寸
	 *!/
    size: PropTypes.oneOf(['sm', 'md', 'xg', 'lg', 'small', 'large', 'middle']),
    /!**
	 * @title 样式
	 *!/
    style: PropTypes.object,
    /!**
	 * @title 形状
	 *!/
    shape: PropTypes.oneOf(['block', 'round', 'border', 'squared', 'floating', 'pillRight', 'pillLeft', 'icon']),

    bordered: PropTypes.bool,
    /!**
	 * @title 是否有icon图标
	 *!/
    icon: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    /!**
	 * @title 是否有loading状态
	 *!/
    loading: PropTypes.bool,
    /!**
	 * @title 是否有loading提示
	 *!/
    loadingText: PropTypes.string,
    /!**
	 * @title 类型
	 *!/
    colors: PropTypes.oneOf(['primary', 'secondary', 'accent', 'success', 'info', 'warning', 'danger', 'dark', 'light', 'default']),
    /!**
	 * @title 是否禁用
	 * @veIgnore
	 *!/
    disabled: PropTypes.bool,
    /!**
	 * @title 类名
	 * @veIgnore
	 *!/
    className: PropTypes.string,

    /!**
	 * @title <button> 的 type
	 * @veIgnore
	 *!/
    htmlType: PropTypes.oneOf(['submit', 'button', 'reset']),
    /!**
	 * @title 类型
	 *!/
    type: PropTypes.oneOf(['default', 'ghost', 'danger', 'primary', 'dashed', 'text', 'link']),
    /!**
	 * @title 是否作为form的提交按钮
	 *!/
    isSubmit: PropTypes.bool,
    /!**
	 * @title 是否危险按钮
	 *!/
    danger: PropTypes.bool,
    /!**
	 * @title 是否幽灵按钮
	 *!/
    ghost: PropTypes.bool,
    /!**
	 * @title 是否为块级按钮
	 *!/
    block: PropTypes.bool,
    /!**
	 * @title 链接按钮的链接
	 *!/
    href: PropTypes.string,
    /!**
     * @title fieldid
     *!/
    fieldid: PropTypes.string
}*/

const defaultProps = {
    disabled: false,
    htmlType: 'button',
    bordered: false,
    isSubmit: false,
    loading: false,
    danger: false,
    ghost: false,
    loadingText: "",
    size: null,
    shape: null,
    icon: null,
    colors: null,
    type: null,
    href: null,
    fieldid: undefined,
    maxWidth: false
}

const colorsMap = {
        primary: 'primary',
        secondary: 'secondary',
        accent: 'accent',
        success: 'success',
        info: 'info',
        warning: 'warning',
        danger: 'danger',
        dark: 'dark',
        light: 'light'
    },
    typeMap = {
        default: 'default',
        ghost: 'ghost',
        danger: 'danger',
        dashed: 'dashed',
        text: 'text',
        link: 'link',
        // tool: 'tool',
        primary: 'primary',
        plainText: 'plainText'
    },
    shapeMap = {
        block: 'block',
        round: 'round',
        border: 'border',
        squared: 'squared',
        floating: 'floating',
        pillRight: 'pill-right',
        pillLeft: 'pill-left',
        icon: 'icon',
        circle: 'icon'
    };

const ButtonComponent: React.FC<ButtonProps> = (props) => {
    const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
    const {
        colors,
        shape,
        className,
        size,
        bordered,
        children,
        htmlType,
        clsPrefix,
        loading,
        loadingText,
        icon,
        danger,
        ghost,
        type,
        block,
        href,
        disabled,
        uirunmode,
        title,
        maxWidth,
        ...others
    } = props;

    const iconProps: Pick<ButtonProps, "id" | "fieldid"> = {};
    if (props.id) {
        iconProps.id = `${props.id}_icon`;
    }
    if (props.fieldid) {
        iconProps.fieldid = `${props.fieldid}_icon`;
    }

    const loadingProps: Pick<ButtonProps, "id" | "fieldid"> = {};
    if (props.id) {
        loadingProps.id = `${props.id}_loading_icon`;
    }
    if (props.fieldid) {
        loadingProps.fieldid = `${props.fieldid}_loading_icon`;
    }

    const isIconNode = (child?: ReactElement<React.HTMLAttributes<any> | undefined> | string) => {
        if (!child) return;
        if (React.isValidElement(child)) {
            const childProps = { ...iconProps };
            childProps.fieldid = child.props?.fieldid ?? childProps.fieldid;
            childProps.id = child.props?.id ?? childProps.id;
            const cloneChild = cloneElement(child, childProps);
            return (<span className={`${clsPrefix}-icon-cls-wrapper`}>{cloneChild}</span>);
        } else {
            return (<span className={`${clsPrefix}-icon-cls-wrapper`}><Icon {...iconProps} type={child as string}/></span>);
        }
    };

    const classes = classnames(clsPrefix, {
        [`${className}`]: !!className,
        [`${clsPrefix}-danger`]: danger || typeMap[type!] === 'danger',
        [`${clsPrefix}-disabled`]: disabled,
        [`${clsPrefix}-ghost`]: ghost || typeMap[type!] === 'ghost',
        [`${clsPrefix}-border`]: bordered || shapeMap[shape!] === 'border',
        [`${clsPrefix}-block`]: block || shapeMap[shape!] === 'block',
        [`${clsPrefix}-text`]: typeMap[type!] === 'link',
        [`${clsPrefix}-loading`]: loading,
        [`${clsPrefix}-${colorsMap[type as ButtonColors]}`]: colorsMap[type as ButtonColors],
        [`${clsPrefix}-${setComponentSize(size!)}`]: setComponentSize(size!),
        [`${clsPrefix}-${shapeMap[shape!]}`]: shapeMap[shape!],
        [`${clsPrefix}-${colorsMap[colors!]}`]: colorsMap[colors!],
        [`${clsPrefix}-${typeMap[type!]}`]: typeMap[type!],
        [`${clsPrefix}-has-max-width`]: maxWidth,
        [`${clsPrefix}-icon-cls`]: !!icon,
        [`${clsPrefix}-icon-only`]: !!icon && !children && children !== 0 && !(loading && loadingText)
    });

    const loadingIcon = <span className={`${clsPrefix}-loading-wrapper`}><Icon {...loadingProps} type="uf-loadingstate"/></span>;
    const beeIcon = isIconNode(icon);
    const hasChildOrLoadingText = !!children || children === 0 || (loading && loadingText);
    const adapterNid = getNid(props);

    if (type === 'link' && href !== null && props.href !== undefined) {
        return (
            <a
                type={htmlType}
                ref={buttonRef as React.RefObject<HTMLAnchorElement>}
                title={title}
                href={href}
                disabled={uirunmode === 'design' ? false : disabled}
                className={classes}
                {...omit(others, ["isText", "isSubmit"])}
            >
                {loading ? loadingIcon : beeIcon}
                {hasChildOrLoadingText && <span className={`${clsPrefix}-text-wrap`}>
                    {loading && loadingText ? loadingText : children}
                </span>}
            </a>
        );
    }

    return (
        <button
            type={htmlType}
            ref={buttonRef as React.RefObject<HTMLButtonElement>}
            className={classes}
            {...omit(others, ["isText", "isSubmit", 'maxWidth'] as any)}
            disabled={uirunmode === 'design' ? false : disabled}
            {...adapterNid}
            title={title}
        >
            {loading ? loadingIcon : beeIcon}
            {hasChildOrLoadingText && <span className={`${clsPrefix}-text-wrap`}>
                {loading && loadingText ? loadingText : children}
            </span>}
        </button>
    );
};

const Button = WithConfigConsumer()(WebUI({ name: "button", defaultProps })(ButtonComponent));

Button.Group = ButtonGroup;

export default Button;
